import React from 'react';
import { Share2, Shield, ExternalLink, Clock, User, CalendarCheck, ClipboardList, Bell, Smartphone } from 'lucide-react';
import { formatTime, formatDateLabel } from '../services/slotsService';

// "sábado 6 jun" → "Sábado 6 jun" (solo la inicial; capitalize de CSS rompe "de").
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const APP_STORE_URL = 'https://apps.apple.com/mx/app/daisies/id1608605255';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=health.neuron.daisies';

function AppleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282z" />
    </svg>
  );
}

function PlayMark() {
  return (
    <svg width="16" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.2 2.3a1 1 0 0 0-.7 1v17.4a1 1 0 0 0 1.5.9l15-8.7a1 1 0 0 0 0-1.8l-15-8.7a1 1 0 0 0-.8-.3z" />
    </svg>
  );
}

function StoreButtons() {
  return (
    <div className="store-row">
      <a className="store-btn" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Descargar en App Store">
        <AppleMark />
        <span className="store-btn-label"><small>Descárgala en</small><b>App Store</b></span>
      </a>
      <a className="store-btn" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Descargar en Google Play">
        <PlayMark />
        <span className="store-btn-label"><small>Disponible en</small><b>Google Play</b></span>
      </a>
    </div>
  );
}

/* Doctoras + horarios que la usuaria marcó (o el día tentativo) y que está
   esperando que se liberen. */
function WaitingFor({ slots, date, specialty }) {
  if (!slots.length && !date) return null;

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--daisies-100)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: slots.length ? 4 : 8 }}>
        <Bell size={14} color="var(--daisies)" />
        <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
          Te avisaremos cuando se libere
        </p>
      </div>

      {slots.length > 0 ? (
        <div>
          {slots.map((s) => (
            <div className="waiting-row" key={s.id}>
              {s.professional?.photo ? (
                <img className="waiting-avatar" src={s.professional.photo} alt={s.professional.name} />
              ) : (
                <span className="waiting-avatar"><User size={18} color="var(--daisies)" /></span>
              )}
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.professional?.name || (specialty?.name || 'Tu profesional')}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {capFirst(formatDateLabel(s.date))}
                </p>
              </div>
              <span className="waiting-time">
                <Clock size={12} />
                {formatTime(s.init)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="waiting-row" style={{ paddingBottom: 0 }}>
          <span className="waiting-avatar"><CalendarCheck size={18} color="var(--daisies)" /></span>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              {capFirst(formatDateLabel(date))}
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              El día que elegiste
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function QueueConfirmation({ formData }) {
  const slot = formData?.slot;
  const waitingSlots = (formData?.waitingSlots || []).slice(0, 3);
  const waitingDate = formData?.waitingDate || null;

  const handleGoToApp = () => {
    const params = new URLSearchParams({
      leadId: (formData.leadId || '').toString(),
      email: formData.email || '',
      password: formData.password || '',
      countryId: (formData.countryId || 156).toString(),
    });
    window.location.href = `https://app.daisies.mx/authPage?${params.toString()}`;
  };

  const handleShare = () => {
    const text = '🌼 En Daisies hay consultas de salud gratuitas para quienes más lo necesitan. Reserva la tuya → ';
    const url = 'https://social.daisies.mx';
    if (navigator.share) {
      navigator.share({ title: 'Daisies Social', text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + url)}`, '_blank');
    }
  };

  /* ─────────────── Vista: consulta reservada (slot) ─────────────── */
  if (slot) {
    return (
      <div className="page-container" style={{ paddingBottom: 40 }}>
        <div className="animate-fadeInUp" style={{ textAlign: 'center', marginBottom: 32 }}>
          <div className="animate-float" style={{
            width: 80, height: 80, borderRadius: '50%', background: '#D1F5E0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 36,
          }}>✅</div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.5px', marginBottom: 6 }}>
            ¡Consulta reservada!
          </h1>
          <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {formData.nombre.toLowerCase()}, tu consulta gratuita ha sido confirmada
          </p>
        </div>

        <div className="animate-fadeInUp-1">
          <div className="card" style={{ padding: '24px 20px' }}>
            <p className="ds-caption" style={{ marginBottom: 14 }}>TU CONSULTA</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: slot?.professional ? 16 : 0 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: formData.specialty?.color || 'var(--daisies-50)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, flexShrink: 0,
              }}>
                {formData.specialty?.emoji || '🩺'}
              </div>
              <div>
                <strong style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'block' }}>
                  {formData.specialty?.name || 'Consulta'}
                </strong>
                <span style={{ fontSize: 13, color: 'var(--daisies)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={13} />
                  {formatTime(slot.time)} → {formatTime(slot.timeEnd)}
                </span>
              </div>
            </div>
            {slot?.professional && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--surface)', borderRadius: 12 }}>
                {slot.professional.photo ? (
                  <img src={slot.professional.photo} alt={slot.professional.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid white' }} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--daisies-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={16} color="var(--daisies)" />
                  </div>
                )}
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 1 }}>{slot.professional.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Tu profesional de salud</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Toma tu consulta desde la app */}
        <div className="animate-fadeInUp-2" style={{ marginTop: 20 }}>
          <div className="card-gradient" style={{ padding: '24px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Smartphone size={18} />
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Toma tu consulta desde la app</h3>
            </div>
            <p style={{ fontSize: 13, opacity: 0.92, lineHeight: 1.6, marginBottom: 16 }}>
              Tu consulta es por videollamada en la app de Daisies. Descárgala y entra con tu correo para completar tu expediente antes de la cita.
            </p>
            <StoreButtons />
          </div>
        </div>

        <div className="animate-fadeInUp-4" style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button className="btn-primary press-scale" onClick={handleGoToApp} id="go-to-app">
            <ExternalLink size={18} />
            Entrar a completar mi expediente
          </button>
          <button className="btn-secondary press-scale" onClick={handleShare} id="share-button">
            <Share2 size={16} />
            Compartir con alguien que lo necesite
          </button>
        </div>

        <CredentialsAndFooter formData={formData} />
      </div>
    );
  }

  /* ─────────────── Vista: en la fila virtual (sin slot) ─────────────── */
  return (
    <div className="page-container" style={{ paddingBottom: 40 }}>
      {/* Hero con ilustración de marca */}
      <div className="animate-fadeInUp" style={{ textAlign: 'center', marginBottom: 28 }}>
        <img
          className="confirm-illustration"
          src="/images/fila-whatsapp.jpg"
          alt="Chica sonriendo al revisar su teléfono, esperando el aviso de Daisies por WhatsApp"
        />
        <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.5px', marginBottom: 6 }}>
          ¡Ya estás en la fila!
        </h1>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: 340, margin: '0 auto' }}>
          {formData.nombre.toLowerCase()}, guardamos tu lugar. En cuanto se libere un horario, eres la siguiente.
        </p>
        <p style={{ fontSize: 12, marginTop: 10, fontWeight: 600, color: formData.phoneVerified ? '#065F46' : 'var(--text-secondary)' }}>
          {formData.phoneVerified
            ? '✓ WhatsApp verificado: por ahí te avisaremos'
            : 'Tip: verifica tu WhatsApp desde la app para no perderte el aviso'}
        </p>
      </div>

      {/* Tu lugar en la fila + doctoras/horarios que esperas */}
      <div className="animate-fadeInUp-1">
        <div className="card" style={{ padding: '24px 20px' }}>
          <p className="ds-caption" style={{ marginBottom: 14 }}>TU LUGAR EN LA FILA</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: formData.specialty?.color || 'var(--daisies-50)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, flexShrink: 0,
            }}>
              {formData.specialty?.emoji || '🩺'}
            </div>
            <div>
              <strong style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'block' }}>
                {formData.specialty?.name || 'Consulta'}
              </strong>
              <span style={{ fontSize: 13, color: 'var(--daisies)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <ClipboardList size={13} />
                Lista de espera activa
              </span>
            </div>
          </div>

          <WaitingFor slots={waitingSlots} date={waitingDate} specialty={formData.specialty} />
        </div>
      </div>

      {/* Descarga la app (momento de marca) */}
      <div className="animate-fadeInUp-2" style={{ marginTop: 20 }}>
        <div className="card-gradient" style={{ padding: '24px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Smartphone size={18} />
            <h3 style={{ fontSize: 15, fontWeight: 700 }}>Descarga la app desde ahora</h3>
          </div>
          <p style={{ fontSize: 13, opacity: 0.92, lineHeight: 1.6, marginBottom: 16 }}>
            Cuando se libere tu horario te avisamos por WhatsApp. Tu consulta es por videollamada en la app, así que tenerla lista te deja tomarla en un toque.
          </p>
          <StoreButtons />
        </div>
      </div>

      {/* ¿Qué sigue? */}
      <div className="animate-fadeInUp-3" style={{ marginTop: 20 }}>
        <div className="card" style={{ padding: '24px 20px' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarCheck size={18} color="var(--daisies)" />
            ¿Qué sigue?
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              ['Descarga la app y completa tu expediente.', 'Así la doctora te conoce antes de tu consulta y todo va más rápido.'],
              ['Te escribimos por WhatsApp.', 'En cuanto se libere uno de tus horarios, te llega el aviso para confirmar.'],
              ['Entras y tomas tu consulta.', 'A la hora acordada abres la app y tu doctora está del otro lado por videollamada.'],
            ].map(([title, body], i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', background: 'var(--daisies-50)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: 'var(--daisies)', flexShrink: 0,
                }}>{i + 1}</div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{title}</strong>{' '}{body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Compromiso mutuo (versión quieter en brand-tint) */}
      <div className="animate-fadeInUp-4" style={{ marginTop: 20 }}>
        <div style={{ background: 'var(--brand-tint)', borderRadius: 'var(--radius-card)', padding: '22px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Shield size={18} color="var(--brand-deep)" />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--brand-deep)' }}>Nuestro compromiso contigo</h3>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>
            Este servicio existe porque creemos que la salud es un derecho. Cada consulta es tiempo real de una
            profesional de salud. Si la tomas, <strong>te pedimos que asistas</strong>. Si no puedes, avísanos
            para darle tu lugar a alguien más.
          </p>
        </div>
      </div>

      {/* Acciones secundarias */}
      <div className="animate-fadeInUp-5" style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button className="btn-secondary press-scale" onClick={handleShare} id="share-button">
          <Share2 size={16} />
          Compartir con alguien que lo necesite
        </button>
        <button
          onClick={() => window.location.href = '/'}
          style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', padding: '8px' }}
        >
          Volver al inicio
        </button>
      </div>

      <CredentialsAndFooter formData={formData} />
    </div>
  );
}

/* Recordatorio de credenciales + footer (compartido por ambas vistas) */
function CredentialsAndFooter({ formData }) {
  return (
    <>
      <div className="animate-fadeInUp-5" style={{ marginTop: 20 }}>
        <div style={{ padding: '14px 16px', background: 'var(--brand-tint)', borderRadius: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <ClipboardList size={14} color="var(--daisies)" />
            <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--daisies)' }}>Tus datos de acceso</p>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Correo: <strong style={{ color: 'var(--text-primary)' }}>{formData.email}</strong>
          </p>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Contraseña: la que acabas de crear
          </p>
        </div>
      </div>

      <div className="animate-fadeInUp-5" style={{ marginTop: 40, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
          <img src="/images/flor.png" alt="Daisies" style={{ height: 28, width: 'auto' }} />
          <img src="/images/letras.png" alt="Daisies" style={{ height: 20, width: 'auto' }} />
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          💜 Hecho con amor para quienes más lo necesitan
        </p>
      </div>
    </>
  );
}
